/**
 * IAB vertical targeting — a hierarchy of checkboxes over the full IAB content taxonomy (id + friendly
 * name). Collapsible by default; typing filters to a flat matching list. Controlled: `value` is the set of
 * selected category ids.
 */

import { useMemo, useState } from 'react';
import { IAB_CATEGORIES, type IabCategory } from '../lib/iab-categories';

export function IabTree(
  { value, onChange, disabled = false }: { value: string[]; onChange: (ids: string[]) => void; disabled?: boolean },
): JSX.Element {
  const byParent = useMemo(() => {
    const m = new Map<string, IabCategory[]>();
    for (const c of IAB_CATEGORIES) { const k = c.parent; if (!m.has(k)) m.set(k, []); m.get(k)!.push(c); }
    return m;
  }, []);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [q, setQ] = useState('');
  const sel = new Set(value);

  const toggle = (id: string) => {
    if (disabled) return;
    const n = new Set(value);
    if (n.has(id)) n.delete(id); else n.add(id);
    onChange([...n]);
  };
  const toggleExpand = (id: string) => setExpanded((p) => {
    const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n;
  });

  const Row = ({ c }: { c: IabCategory }) => (
    <label className="iab-node">
      <input type="checkbox" checked={sel.has(c.id)} disabled={disabled} onChange={() => toggle(c.id)} />
      <span className="iab-id">{c.id}</span>
      <span>{c.name}</span>
    </label>
  );

  const query = q.trim().toLowerCase();
  const searchBox = (
    <input className="iab-search" value={q} placeholder="Filter IAB categories by name or ID…" onChange={(e) => setQ(e.target.value)} />
  );

  if (query) {
    const matches = IAB_CATEGORIES.filter((c) => c.id.toLowerCase().includes(query) || c.name.toLowerCase().includes(query)).slice(0, 300);
    return (
      <div className={`iab-tree${disabled ? ' disabled' : ''}`}>
        {searchBox}
        <div className="iab-scroll">{matches.map((c) => <Row key={c.id} c={c} />)}</div>
        <div className="iab-foot">{value.length} selected</div>
      </div>
    );
  }

  const renderNode = (c: IabCategory, depth: number): JSX.Element => {
    const kids = byParent.get(c.id) ?? [];
    const open = expanded.has(c.id);
    return (
      <div key={c.id} className="iab-branch" style={{ marginLeft: depth ? 16 : 0 }}>
        <div className="iab-row">
          {kids.length
            ? <button type="button" className="iab-toggle" onClick={() => toggleExpand(c.id)} aria-label={open ? 'Collapse' : 'Expand'}>{open ? '▾' : '▸'}</button>
            : <span className="iab-toggle-sp" />}
          <Row c={c} />
        </div>
        {open && kids.map((k) => renderNode(k, depth + 1))}
      </div>
    );
  };

  return (
    <div className={`iab-tree${disabled ? ' disabled' : ''}`}>
      {searchBox}
      <div className="iab-scroll">{(byParent.get('') ?? []).map((c) => renderNode(c, 0))}</div>
      <div className="iab-foot">{value.length} selected</div>
    </div>
  );
}
